import React, { useState, useRef } from 'react';
import { 
  SavedScript, 
  PineStorage, 
  saveScript, 
  updateScript, 
  deleteScript, 
  toggleRobot,
  exportScripts,
  importScripts,
  savePineStorage,
} from '../lib/pineStorage';

interface Props {
  storage: PineStorage;
  onStorageChange: (storage: PineStorage) => void;
  onLoadScript: (script: SavedScript) => void;
  onClose: () => void;
  currentCode: string;
}

export default function PineScriptManager({ storage, onStorageChange, onLoadScript, onClose, currentCode }: Props) {
  const [tab, setTab] = useState<'scripts' | 'robots' | 'import'>('scripts');
  const [saveName, setSaveName] = useState('');
  const [saveDescription, setSaveDescription] = useState('');
  const [isRobot, setIsRobot] = useState(false);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [editingScript, setEditingScript] = useState<SavedScript | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const scripts = storage.scripts.filter(s => !s.isRobot);
  const robots = storage.scripts.filter(s => s.isRobot);

  const handleSave = () => {
    if (!saveName.trim()) {
      alert('Please enter a name for your script');
      return;
    }

    const newStorage = saveScript(storage, {
      name: saveName.trim(),
      description: saveDescription.trim() || undefined,
      code: currentCode,
      isRobot,
    });

    onStorageChange(newStorage);
    savePineStorage(newStorage);
    setSaveName('');
    setSaveDescription('');
    setIsRobot(false);
    setShowSaveForm(false);
    alert('✅ Script saved successfully!');
  };

  const handleDelete = (id: string) => {
    if (!confirm('Delete this script?')) return;
    const newStorage = deleteScript(storage, id);
    onStorageChange(newStorage);
    savePineStorage(newStorage);
  };

  const handleToggleRobot = (id: string) => {
    const script = storage.scripts.find(s => s.id === id);
    if (!script) return;
    
    const isActive = storage.activeRobots.includes(id);
    
    if (!isActive) {
      // Starting robot
      if (!script.settings?.symbol || !script.settings?.platform) {
        alert('Please configure symbol and platform for this robot first');
        setEditingScript(script);
        return;
      }
    }

    const newStorage = toggleRobot(storage, id);
    onStorageChange(newStorage);
    savePineStorage(newStorage);
    
    if (isActive) {
      alert(`⏹️ Robot "${script.name}" stopped`);
    } else {
      alert(`🚀 Robot "${script.name}" started on ${script.settings?.symbol}`);
    }
  };

  const handleUpdateSettings = (id: string, settings: SavedScript['settings']) => {
    const newStorage = updateScript(storage, id, { settings });
    onStorageChange(newStorage);
    savePineStorage(newStorage);
    setEditingScript(null);
  };

  const handleExport = () => {
    const data = exportScripts(storage.scripts);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'kit-pine-scripts.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      
      // Check if it's a .pine file (raw Pine Script)
      if (file.name.endsWith('.pine') || file.name.endsWith('.txt')) {
        setSaveName(file.name.replace(/\.(pine|txt)$/, ''));
        setShowSaveForm(true);
        // Put the content in the editor
        onLoadScript({
          id: 'temp',
          name: file.name,
          code: content,
          createdAt: '',
          updatedAt: '',
          isRobot: false,
        });
        onClose();
        alert('📝 Pine Script loaded! Click "Save Script" to save it permanently.');
        return;
      }

      // JSON import
      try {
        const imported = importScripts(content);
        if (imported.length === 0) {
          alert('No valid scripts found in file');
          return;
        }

        const newStorage = {
          ...storage,
          scripts: [...storage.scripts, ...imported],
        };
        onStorageChange(newStorage);
        savePineStorage(newStorage);
        alert(`✅ Imported ${imported.length} script(s)`);
      } catch (err) {
        alert('Failed to import: Invalid file format');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-800">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <span className="text-green-400">🌲</span>
            Pine Script Manager
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-xl">✕</button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-800">
          <button
            onClick={() => setTab('scripts')}
            className={`flex-1 py-3 text-sm font-medium ${tab === 'scripts' ? 'text-orange-500 border-b-2 border-orange-500' : 'text-gray-500'}`}
          >
            📊 Indicators ({scripts.length})
          </button>
          <button
            onClick={() => setTab('robots')}
            className={`flex-1 py-3 text-sm font-medium ${tab === 'robots' ? 'text-orange-500 border-b-2 border-orange-500' : 'text-gray-500'}`}
          >
            🤖 Robots ({robots.length})
          </button>
          <button
            onClick={() => setTab('import')}
            className={`flex-1 py-3 text-sm font-medium ${tab === 'import' ? 'text-orange-500 border-b-2 border-orange-500' : 'text-gray-500'}`}
          >
            📥 Import/Export
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {/* Save Form */}
          {showSaveForm && (
            <div className="bg-gray-800 rounded-lg p-4 mb-4">
              <h3 className="font-medium mb-3">💾 Save Current Script</h3>
              <input
                type="text"
                placeholder="Script name"
                value={saveName}
                onChange={e => setSaveName(e.target.value)}
                className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 mb-2"
              />
              <input
                type="text"
                placeholder="Description (optional)"
                value={saveDescription}
                onChange={e => setSaveDescription(e.target.value)}
                className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 mb-2"
              />
              <label className="flex items-center gap-2 mb-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isRobot}
                  onChange={e => setIsRobot(e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="text-sm">This is an auto-trading robot (not just indicator)</span>
              </label>
              <div className="flex gap-2">
                <button onClick={handleSave} className="flex-1 bg-green-600 hover:bg-green-700 py-2 rounded font-medium">
                  Save Script
                </button>
                <button onClick={() => setShowSaveForm(false)} className="px-4 bg-gray-700 hover:bg-gray-600 py-2 rounded">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {!showSaveForm && tab !== 'import' && (
            <button
              onClick={() => setShowSaveForm(true)}
              className="w-full bg-orange-500/20 border-2 border-dashed border-orange-500/50 hover:border-orange-500 text-orange-400 py-3 rounded-lg mb-4"
            >
              + Save Current Script
            </button>
          )}

          {/* Scripts List */}
          {tab === 'scripts' && (
            <div className="space-y-2">
              {scripts.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  No saved indicators yet.<br />
                  Write a Pine Script and save it here!
                </div>
              ) : (
                scripts.map(script => (
                  <div key={script.id} className="bg-gray-800 rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium">{script.name}</div>
                        {script.description && (
                          <div className="text-sm text-gray-500">{script.description}</div>
                        )}
                        <div className="text-xs text-gray-600 mt-1">
                          Updated: {new Date(script.updatedAt).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => { onLoadScript(script); onClose(); }}
                          className="text-blue-400 hover:text-blue-300 text-sm"
                        >
                          Load
                        </button>
                        <button
                          onClick={() => handleDelete(script.id)}
                          className="text-red-400 hover:text-red-300 text-sm"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Robots List */}
          {tab === 'robots' && (
            <div className="space-y-2">
              {robots.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  No trading robots yet.<br />
                  Save a Pine Script with "auto-trading robot" checked!
                </div>
              ) : (
                robots.map(script => {
                  const isActive = storage.activeRobots.includes(script.id);
                  return (
                    <div key={script.id} className={`rounded-lg p-3 ${isActive ? 'bg-green-900/30 border border-green-500/50' : 'bg-gray-800'}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{script.name}</span>
                            {isActive && (
                              <span className="text-xs bg-green-600 px-2 py-0.5 rounded animate-pulse">● RUNNING</span>
                            )}
                          </div>
                          {script.description && (
                            <div className="text-sm text-gray-500">{script.description}</div>
                          )}
                          {script.settings?.symbol && (
                            <div className="text-xs text-gray-400 mt-1">
                              {script.settings.symbol} • {script.settings.timeframe || '1H'} • {script.settings.platform}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setEditingScript(script)}
                            className="text-gray-400 hover:text-white text-sm"
                          >
                            ⚙️
                          </button>
                          <button
                            onClick={() => handleToggleRobot(script.id)}
                            className={`px-3 py-1 rounded text-sm ${isActive ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}
                          >
                            {isActive ? 'Stop' : 'Start'}
                          </button>
                          <button
                            onClick={() => { onLoadScript(script); onClose(); }}
                            className="text-blue-400 hover:text-blue-300 text-sm"
                          >
                            Edit
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Import/Export */}
          {tab === 'import' && (
            <div className="space-y-4">
              <div className="bg-gray-800 rounded-lg p-4">
                <h3 className="font-medium mb-3">📥 Import Scripts</h3>
                <p className="text-sm text-gray-400 mb-3">
                  Import Pine Scripts from .pine, .txt, or .json files
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pine,.txt,.json"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full bg-blue-600 hover:bg-blue-700 py-3 rounded font-medium"
                >
                  📁 Choose File to Import
                </button>
              </div>

              <div className="bg-gray-800 rounded-lg p-4">
                <h3 className="font-medium mb-3">📤 Export Scripts</h3>
                <p className="text-sm text-gray-400 mb-3">
                  Download all your scripts as a JSON file
                </p>
                <button
                  onClick={handleExport}
                  disabled={storage.scripts.length === 0}
                  className="w-full bg-purple-600 hover:bg-purple-700 py-3 rounded font-medium disabled:opacity-50"
                >
                  💾 Export All Scripts ({storage.scripts.length})
                </button>
              </div>

              <div className="bg-gray-800/50 rounded-lg p-4">
                <h3 className="font-medium mb-2">📝 Supported Formats</h3>
                <ul className="text-sm text-gray-400 space-y-1">
                  <li>• <strong>.pine</strong> - Raw Pine Script files</li>
                  <li>• <strong>.txt</strong> - Text files with Pine Script code</li>
                  <li>• <strong>.json</strong> - K.I.T. export format (multiple scripts)</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Robot Settings Modal */}
        {editingScript && (
          <div className="absolute inset-0 bg-black/80 flex items-center justify-center p-4">
            <div className="bg-gray-900 rounded-lg w-full max-w-md p-4">
              <h3 className="font-bold mb-4">⚙️ Robot Settings: {editingScript.name}</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-500">Symbol</label>
                  <input
                    type="text"
                    placeholder="e.g., BTCUSDT"
                    defaultValue={editingScript.settings?.symbol}
                    onChange={e => editingScript.settings = { ...editingScript.settings, symbol: e.target.value }}
                    className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 mt-1"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Timeframe</label>
                  <select
                    defaultValue={editingScript.settings?.timeframe || '60'}
                    onChange={e => editingScript.settings = { ...editingScript.settings, timeframe: e.target.value }}
                    className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 mt-1"
                  >
                    <option value="1">1m</option>
                    <option value="5">5m</option>
                    <option value="15">15m</option>
                    <option value="60">1H</option>
                    <option value="240">4H</option>
                    <option value="D">1D</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Platform</label>
                  <select
                    defaultValue={editingScript.settings?.platform || 'paper'}
                    onChange={e => editingScript.settings = { ...editingScript.settings, platform: e.target.value }}
                    className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 mt-1"
                  >
                    <option value="paper">Paper Trading</option>
                    <option value="binance">Binance</option>
                    <option value="bybit">Bybit</option>
                    <option value="mt5">MetaTrader 5</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => handleUpdateSettings(editingScript.id, editingScript.settings)}
                  className="flex-1 bg-green-600 hover:bg-green-700 py-2 rounded"
                >
                  Save Settings
                </button>
                <button
                  onClick={() => setEditingScript(null)}
                  className="px-4 bg-gray-700 hover:bg-gray-600 py-2 rounded"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
